import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AdminFeedbackDetail,
  AdminFeedbackPage,
  AdminFeedbackQuery,
  TriageStatus,
} from './feedback.types';
import { StatusMeta } from '../status/status-meta.types';

/**
 * Chip labels and colours for a report's triage status.
 *
 * Labels match the backend's `TriageStatus` choices verbatim — a chip and a
 * filter option that word the same state differently is how a list starts
 * disagreeing with itself.
 */
export const TRIAGE_STATUS_META: Record<TriageStatus, StatusMeta> = {
  NEW: { label: 'New', variant: 'warning' },
  TRIAGED: { label: 'Triaged', variant: 'default' },
  IN_PROGRESS: { label: 'In progress', variant: 'default' },
  // FLOW-353: open, so the open-state tone — `neutral` is the closed states'.
  NEEDS_DISCUSSION: { label: 'Needs discussion', variant: 'default' },
  RESOLVED: { label: 'Resolved', variant: 'success' },
  WONT_FIX: { label: "Won't fix", variant: 'neutral' },
  DUPLICATE: { label: 'Duplicate', variant: 'neutral' },
};

/** Admin feedback API: the triage worklist, one report, and its decisions. */
@Injectable({ providedIn: 'root' })
export class AdminFeedbackService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/feedback/admin/reports`;

  list(query: AdminFeedbackQuery = {}): Observable<AdminFeedbackPage> {
    let params = new HttpParams();
    if (query.page) params = params.set('page', query.page);
    if (query.page_size) params = params.set('page_size', query.page_size);
    if (query.triage_status) params = params.set('triage_status', query.triage_status);
    if (query.kind) params = params.set('kind', query.kind);
    if (query.search) params = params.set('search', query.search);
    // Only sent when false: the backend already defaults to the open worklist.
    if (query.open_only === false) params = params.set('open_only', 'false');
    return this.http.get<AdminFeedbackPage>(`${this.base}/`, { params });
  }

  get(shortId: string): Observable<AdminFeedbackDetail> {
    return this.http.get<AdminFeedbackDetail>(`${this.base}/${shortId}/`);
  }

  /**
   * Record a triage decision.
   *
   * Returns the report *and* its refreshed history, so the caller renders what
   * the server now holds rather than patching the row it already had.
   */
  retriage(
    shortId: string,
    to_status: TriageStatus,
    note = '',
  ): Observable<AdminFeedbackDetail> {
    return this.http.post<AdminFeedbackDetail>(`${this.base}/${shortId}/`, {
      to_status,
      note,
    });
  }

  /** A time-limited link to the report's screenshot. */
  screenshotUrl(shortId: string): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.base}/${shortId}/screenshot/`);
  }
}
